import {Component, OnInit, TemplateRef} from '@angular/core';
import {Attachment, FrontendDocument, FrontendStatusEnum, Attachments} from '@nachdigal';
import {ActivatedRoute, Router} from '@angular/router';
import {catchError, finalize, first, tap, toArray} from 'rxjs/operators';
import {BehaviorSubject, concat, of} from 'rxjs';
import {AttachmentService} from '../../service/attachment.service';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {NavigationService } from '@utils-lib';
import {DatevNotificationService, DatevNotificationSeverity} from '@datev/ngx-ui-components';
import {TranslateService} from '@ngx-translate/core';
import {PDF_VIEWER_PATH} from '../../../pdf-viewer/pdf-viewer.routing.module';
import {TruncatePipe} from '../../pipes/truncate.pipe';

function isReadonly(frontendDocument: FrontendDocument): boolean {
  return frontendDocument.status === FrontendStatusEnum.SendingConfirmed ||
    frontendDocument.status === FrontendStatusEnum.SendingFinalized ||
    frontendDocument.status === FrontendStatusEnum.Sending ||
    frontendDocument.status === FrontendStatusEnum.SendingSuccessful;
}

@Component({
  selector: 'frageboegen-attachments',
  templateUrl: './attachments.component.html',
  styleUrls: ['./attachments.component.scss']
})
export class AttachmentsComponent implements OnInit {

  static route = 'attachments';

  supportedMimeTypes = ['image/png', 'image/jpeg', 'image/tiff', 'application/pdf'];
  modalRef: BsModalRef;
  taskId?: string;

  loading$ = new BehaviorSubject(false);
  adding$ = new BehaviorSubject(false);
  deleting$ = new BehaviorSubject(false);
  borderHighlight$ = new BehaviorSubject(false);
  uploadDisabled$ = new BehaviorSubject(false);
  deleteDisabled$ = new BehaviorSubject(false);
  taskVolume$ = new BehaviorSubject<number>(0);
  documents$ = new BehaviorSubject<Attachment[]>([]);
  maxDocumentsReached$ = new BehaviorSubject(false);

  data: Attachments = {email:{email:''}};

  constructor(protected readonly attachmentService: AttachmentService,
              protected readonly activatedRoute: ActivatedRoute,
              public readonly modalService: BsModalService,
              protected readonly navigationService: NavigationService,
              public readonly notificationService: DatevNotificationService,
              protected readonly router: Router,
              protected readonly translateService: TranslateService) {
    this.activatedRoute.parent.data.subscribe((response) => {
      if (response.config) {
        this.attachmentService.backendConfig = response.config;
      }
    });
    this.taskId = this.activatedRoute.parent.snapshot.paramMap.get('taskId');
    this.documents$ = this.attachmentService.attachments;
    this.documents$.subscribe(documents => {
      this.taskVolume$.next(documents.map(d => d.doc_size)?.reduce((a, b) => a + b, 0));
      const maxReached = documents.length >= this.attachmentService.backendConfig.document_count;
      this.maxDocumentsReached$.next(maxReached);
      this.uploadDisabled$.next(maxReached);
    });
  }

  ngOnInit() {
    this.initData();
    this.loadTaskAttachments();
    if (isReadonly(this.activatedRoute.parent.snapshot.data.document.status)) {
      this.uploadDisabled$.next(true);
      this.deleteDisabled$.next(true);
    }
  }

  public onDrag(drag) {
    this.borderHighlight$.next(drag);
  }

  public isReadOnlyWrapper(): boolean{
   return isReadonly(this.activatedRoute.parent.snapshot.data.document.status);
  }

  openDeletingDialog(template: TemplateRef<any>, attachment: Attachment) {
    this.modalRef = this.modalService.show<Attachment>(template, {
      initialState: attachment,
      class: 'modal-dialog-centered'
    });
  }

  public displayPreview(att: Attachment): void {
    this.router.navigate([PDF_VIEWER_PATH, this.activatedRoute.snapshot.root.firstChild?.routeConfig.path, this.taskId, att.doc_id]);
  }

  //------------be-requests-----------------------
  loadTaskAttachments() {
    this.loading$.next(true);
    this.attachmentService.load(this.taskId).pipe(
      first(),
      finalize(() => this.loading$.next(false))
    ).subscribe({
      next: null,
      error: () => this.addGenericError('LOAD', '')
    });
  }

  add(event) {
    //frontend error detection
    this.borderHighlight$.next(false);
    const uploadQueue$ = [];
    const uploadSuccess: string[] = [];
    for (const file of event.target.files) {
      if (!file) {
        this.addUploadError('FILE_CORRUPT', '');
      } else {
        const clientFileErrors = this.getClientFileErrors(file);
        if (clientFileErrors && clientFileErrors.length > 0) {
          this.addMultipleUploadError(clientFileErrors, file.name);
        } else {
          // build file upload query
          const params = {
            'Content-Type': file.type,
            'Doc-Name-Encoding': 'true',
            'Doc-Name': file.name,
            'Doc-Source': 'D:/fake-path',
            'Doc-Source-Id': 'fse-upload'
          };
          uploadQueue$.push(
            this.attachmentService.postAttachment(this.taskId, file, params).pipe(
              tap({
                next: () =>  {
                  uploadSuccess.push(file.name);
                },
                error: err => {
                  const serverFileError = this.getServerFileError(err);
                  this.addUploadError(serverFileError, file.name);
                }
              }),catchError(val => of(val))) // stay with catched errors here, concat() breaks at errors!
          );
        }
      }
    }
    //upload to backend
    this.adding$.next(true);
    this.navigationService.changeDisableStatus(true);
    concat(...uploadQueue$).pipe(toArray(),first()).subscribe({
      next: () => {
        this.adding$.next(false);
        this.navigationService.changeDisableStatus(false);
        if (uploadSuccess.length === 1) { // Single file upload success
          this.addUploadSuccess(uploadSuccess[0]);
        } else { // Multi file upload success
          this.addMultipleUploadSuccess(uploadSuccess);
        }
      }
    });
  }

  remove(file: Attachment) {
    this.deleting$.next(true);
    this.attachmentService.deleteAttachment(this.taskId, file.doc_id).pipe(
      first(),
      finalize(() => {
        this.deleting$.next(false);
        this.modalRef && this.modalRef.hide();
      })).subscribe(
      {
        next: null,
        error: () => this.addGenericError('DELETE', file.doc_name)
      }
    );
  }

  getServerFileError(error): string {
    const code = error?.error?.error;
    const desc = error?.error?.error_description;
    let errorMessage;
    if (code === '400') {
      errorMessage = this.select400err(desc);
    } else if (code === '413') {
      errorMessage = this.select413err(desc);
    } else if (code === '422') {
      errorMessage = this.select422err(desc);
    } else if (code === '423') {
      errorMessage = 'TASK_ALREADY_SUBMITTED';
    } else {
      errorMessage = 'UNKNOWN';
    }
    return errorMessage;
  }

  select422err(desc) {
    if (desc && desc.includes('Wiederholen Sie die Konvertierung der Ursprungsdatei ins PDF-Format und versuchen Sie ' +
      'das Hochladen erneut.')) {
      return 'DOCUMENT_ERROR';
    }else if (desc && desc.includes('Speichern Sie das Dokument als PDF-Datei ab und versuchen Sie das Hochladen erneut.')) {
      return 'FILETYPE_CONTENT';
    } else if (desc && desc.includes('wurde als beschädigt erkannt und konnte nicht')) {
      return 'FILE_CORRUPT';
    } else if (desc && desc.includes('Prüfen Sie den Dateinamen auf Sonderzeichen')) {
      return 'FILENAME_SPECIAL_CHARS';
    } else if (desc && desc.includes('Seiten überschreitet')) {
      return 'PAGES_EXCEEDED';
    } else if (desc && desc.includes('es mit einem Passwort geschützt ist')) {
      return 'PASSWORD_PROTECTED';
    } else {
      return 'UNKNOWN';
    }
  }

  select413err(desc) {
    if (desc && desc.includes('is bigger than the allowed limit')) {
      return 'FILE_TOO_BIG';
    } else if (desc && desc.includes('could not be increased by')) {
      return 'FILECOUNT_EXCEEDED';
    } else if (desc && desc.includes('The size of all documents must be equal or lower')) {
      return 'VOLUME_EXCEEDED';
    } else {
      return 'UNKNOWN';
    }
  }

  select400err(desc: string) {
    if (desc && desc.includes('Document name is longer than the valid length')) {
      return 'FILENAME_TOO_LONG';
    } else if (desc && desc.includes('Doc-Name must be unique')) {
      return 'FILENAME_NOT_UNIQUE';
    } else if (desc && desc.includes('is missing: Doc-Name')) {
      return 'FILENAME_EMPTY';
    } else {
      return 'UNKNOWN';
    }
  }

  addMultipleUploadSuccess(uploadSuccess: string[]) {
    if (uploadSuccess.length) {
      let aggreSuccessMsg = '<ul>';
      aggreSuccessMsg += uploadSuccess.map(file => {
        if(file.length > FILE_NAME_LENGTH) {
          const truncatePipe = new TruncatePipe();
          file = `<span title="${file}">` + truncatePipe.transform(file, FILE_NAME_LENGTH) + '</span>';
        }
        return '<li>' + file + '</li>';
      }).join('') + '</ul>';
      aggreSuccessMsg = this.translateService.instant('COMMON_UI.FRAGEBOGEN.ANHAENGE.SUCCESSES.ADD_MESSAGE_MULTI') + aggreSuccessMsg;
      this.notificationService.close('aggregatedSuccess');
      this.notificationService.showAlert(DatevNotificationSeverity.Success,
        this.translateService.instant('COMMON_UI.FRAGEBOGEN.ANHAENGE.SUCCESSES.ADD_TITLE_MULTI'), null, aggreSuccessMsg, [],
        'aggregatedSuccess', true);
    }
  }

  addMultipleUploadError(errors: string[], fileName: string){
    for (const error of errors) {
      this.addUploadError(error, fileName);
    }
  }

  addUploadError(errorCode: string, fileName: string) {
    this.notificationService.close(errorCode + fileName);
    this.notificationService.showAlert(DatevNotificationSeverity.Error,
      this.translateService.instant('COMMON_UI.FRAGEBOGEN.ANHAENGE.ERRORS.ADD_TITLE'), null,
      this.translateService.instant(TRANSLATE_ERRORS_PREFIX + errorCode, this.getPlaceholderValues(fileName)), [],
      errorCode + fileName, true);
  }

  addUploadSuccess(fileName: string) {
    this.notificationService.close('addSuccess' + fileName);
    this.notificationService.showAlert(
      DatevNotificationSeverity.Success,
      this.translateService.instant('COMMON_UI.FRAGEBOGEN.ANHAENGE.SUCCESSES.ADD_TITLE'), null,
      this.translateService.instant('COMMON_UI.FRAGEBOGEN.ANHAENGE.SUCCESSES.ADD_MESSAGE', this.getPlaceholderValues(fileName)), [],
      'addSuccess' + fileName, true);
  }

  addGenericError(errorCode: string, fileName: string) {
    this.notificationService.close(errorCode);
    this.notificationService.showAlert(DatevNotificationSeverity.Error,
      this.translateService.instant(`translErrors${errorCode}_TITLE`), null,
      this.translateService.instant(`translErrors${errorCode}`, this.getPlaceholderValues(fileName)), [],
      errorCode, true);
  }

  getClientFileErrors(file: File): string[] {
    const  errors = [];
    if (!this.supportedMimeTypes.includes(file.type)) {
      errors.push('MIME_TYPE');
    }
    if (file.name.length > this.attachmentService.backendConfig.document_name_length) {
      errors.push('FILENAME_TOO_LONG');
    }
    if (!!this.documents$.value.find(a => a.doc_name === file.name)) {
      errors.push('FILENAME_NOT_UNIQUE');
    }
    if (file.size > this.attachmentService.backendConfig.document_size) {
      errors.push('FILE_TOO_BIG');
    }
    //task errors
    if (this.documents$.value.length >= this.attachmentService.backendConfig.document_count) {
      errors.push('FILECOUNT_EXCEEDED');
    }
    if (this.taskVolume$.value + file.size >= this.attachmentService.backendConfig.all_documents_size) {
      errors.push('VOLUME_EXCEEDED');
    }
    return errors;
  }

  getPlaceholderValues(fileName: string) {
    if(fileName.length > FILE_NAME_LENGTH) {
      const truncatePipe = new TruncatePipe();
      fileName = `<span title="${fileName}">` + truncatePipe.transform(fileName, FILE_NAME_LENGTH) + '</span>';
    }
    return {
      ...this.attachmentService.backendConfig,
      all_documents_size_mb: Math.round(this.attachmentService.backendConfig.all_documents_size / THOUSAND) / THOUSAND,
      document_size_mb: Math.round(this.attachmentService.backendConfig.document_size / THOUSAND) / THOUSAND,
      fileName
    };
  }

  protected initData() {

    if (this.activatedRoute.snapshot.data.data) {
      if (!this.activatedRoute.snapshot.data.data.attachments) {
        Object.assign(this.activatedRoute.snapshot.data.data,{
          attachments: {
            email: {email: ''}
          }
        });
      }
    }

    this.attachmentService.getUserEmail(this.taskId).subscribe((data)=>{
      this.activatedRoute.snapshot.data.data.attachments = {email:{email:data?.attachments_email}};
      this.data = this.activatedRoute.snapshot.data.data.attachments;
    });
  }
}

const THOUSAND = 1000;
const TRANSLATE_ERRORS_PREFIX = 'COMMON_UI.FRAGEBOGEN.ANHAENGE.ERRORS.';
const FILE_NAME_LENGTH = 30;
